import type { CSSProperties } from "react";
import { useState } from "react";
import { cn } from "../lib/utils";

interface MiniCalendarProps {
  value?: Date;
  onChange?: (date: Date) => void;
  className?: string;
  style?: CSSProperties;
}

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

export function MiniCalendar({ value, onChange, className, style }: MiniCalendarProps) {
  const today = new Date();
  const [cursor, setCursor] = useState<Date>(value ?? today);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  function prev() { setCursor(new Date(year, month - 1, 1)); }
  function next() { setCursor(new Date(year, month + 1, 1)); }

  const cells: (number | null)[] = [...Array(firstDay).fill(null)];
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className={cn("mc-root", className)} style={style}>
      <div className="mc-header">
        <button className="mc-nav" onClick={prev} type="button" aria-label="Previous month">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <span className="mc-month">{MONTHS[month]} {year}</span>
        <button className="mc-nav" onClick={next} type="button" aria-label="Next month">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M5 11l4-4-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      <div className="mc-grid">
        {DAYS.map(d => <span key={d} className="mc-day-name">{d}</span>)}
        {cells.map((day, i) => {
          if (day === null) return <span key={`e-${i}`} />;
          const date = new Date(year, month, day);
          const isToday = isSameDay(date, today);
          const isSelected = value ? isSameDay(date, value) : false;
          return (
            <button
              key={day}
              type="button"
              className={cn("mc-day", isToday && "mc-today", isSelected && "mc-selected")}
              onClick={() => onChange?.(date)}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
